import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";

import LoadingState from "@/components/LoadingState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Camera, Save, X } from "lucide-react";
import { addLog } from "@/logger";
import { transformFormToApiAttributes } from "@/utils/case-utils";
import {
  useCreateCase,
  useAddCaseImage,
  useOperators,
} from "@/services/queries";
import { getCSRFToken } from "@/services/authService";
import { usePhotoState } from "@/hooks/use-photo-state";
import { useFormPersistence } from "@/hooks/use-form-persistence";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { getStoredPhoneId } from "@/utils/deviceId";

// Parishes in Jamaica
const parishes = [
  "Clarendon",
  "Hanover",
  "Kingston",
  "Manchester",
  "Portland",
  "Saint Andrew",
  "Saint Ann",
  "Saint Catherine",
  "Saint Elizabeth",
  "Saint James",
  "Saint Mary",
  "Saint Thomas",
  "Trelawny",
  "Westmoreland",
];

// Mining companies in Jamaica
const miningCompanies = [
  "Jamaica Bauxite Mining Ltd.",
  "Alpart Mining Venture",
  "Windalco",
  "Noranda Jamaica Bauxite Partners",
  "JISCO Alpart",
  "New Day Mining Operations",
  "Coastal Resources Ltd.",
  "Global Minerals Jamaica",
  "Other",
];

// Tags for quick categorization
const availableTags = [
  { value: "water", label: "Water Contamination" },
  { value: "dust", label: "Dust Pollution" },
  { value: "noise", label: "Noise" },
  { value: "erosion", label: "Land Erosion" },
  { value: "displacement", label: "Displacement" },
  { value: "deforestation", label: "Deforestation" },
  { value: "health", label: "Health Issues" },
  { value: "fish-kill", label: "Fish Kill" },
  { value: "wildlife", label: "Wildlife Impact" },
  { value: "property", label: "Property Damage" },
  { value: "cultural", label: "Cultural Site Damage" },
  { value: "other", label: "Other" },
];

// Form schema validation
const formSchema = z.object({
  title: z
    .string()
    .min(5, { message: "Enter a title (at least 5 characters)" }),
  parish: z.string({ required_error: "Select a parish" }),
  operator: z.string({ required_error: "Select a mining operator" }),
  community: z.string().min(2, { message: "Enter the community name" }),
  description: z.string().min(20, {
    message: "Provide a description (at least 20 characters)",
  }),
  impact: z.string().optional(),
  reportedBy: z.string().optional(),
  tags: z
    .array(z.string())
    .min(1, { message: "Select at least one tag" }),
});

type FormValues = z.infer<typeof formSchema>;

const AddCase = () => {
  const navigate = useNavigate();
  const authState = useSelector((state: RootState) => state.auth);
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: createCaseAsync } = useCreateCase();
  const { mutateAsync: addCaseImageAsync } = useAddCaseImage();
  /*  const { photos, clearPhotos, hasPhotos, photoCount, removePhoto } =
    usePhotoState(); */
  const {
    photos,
    removePhoto,
    hasPhotos,
    photoCount,
    loadPhotoAsUrl,
    clearPhotos,
  } = usePhotoState();
  const { saveFormData, loadFormData, clearFormData } = useFormPersistence();
  const hasLoggedAuth = useRef(false);

  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});

  // Use authReady state to determine when to fetch cases
  const shouldFetchCases = authState.authReady;

  useEffect(() => {
    if (!hasLoggedAuth.current) {
      addLog(
        `Auth state: token exists=${!!authState.token}, authReady=${
          authState.authReady
        }`
      );
      hasLoggedAuth.current = true;
    }
  }, [authState.token, authState.authReady]);

  useEffect(() => {
    let cancelled = false;
    async function loadPhotos() {
      const urls: Record<string, string> = {};
      for (const photo of photos) {
        if (cancelled) return;
        const base64Url = await loadPhotoAsUrl(photo.imageUrl);
        if (cancelled) return;
        urls[photo.id] = base64Url;
      }
      if (!cancelled) setPhotoUrls(urls);
    }
    loadPhotos();
    return () => {
      cancelled = true;
    };
  }, [photos, loadPhotoAsUrl]);

  const {
    data: operators = [],
    isLoading,
    isError,
    refetch,
  } = useOperators(shouldFetchCases);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
    defaultValues: {
      title: "",
      parish: "",
      operator: "",
      community: "",
      description: "",
      impact: "",
      reportedBy: "",
      tags: [],
    },
  });

  // Load saved form data on component mount only
  useEffect(() => {
    const savedData = loadFormData();
    if (savedData) {
      form.reset(savedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount
  }, []);

  // Save form data whenever form values change
  const skipNextSave = useRef(false);

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (skipNextSave.current) {
        skipNextSave.current = false;
        return;
      }
      saveFormData(value);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.watch/saveFormData are stable; omit form to avoid re-subscribing every render
  }, [form.watch, saveFormData]);

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      const csrfToken = await getCSRFToken();

      // Get device ID for tracking case ownership
      const deviceId = await getStoredPhoneId();
      addLog(`Submitting case with device_id: ${deviceId}`);

      const transformedData = transformFormToApiAttributes({
        ...data,
        deviceId,
      });
      const caseResponse = (await createCaseAsync(transformedData)) as {
        data?: { id?: string };
      };
      addLog(`case response: ${JSON.stringify(caseResponse, null, 2)}`);
      const caseId = caseResponse?.data?.id;
      if (!caseId) throw new Error("Case created but no ID returned");

      let uploadedCount = 0;
      let failedCount = 0;

      if (photos.length > 0) {
        addLog(`Uploading ${photos.length} images`);

        const dataUrlToBlob = (dataUrl: string): Blob | null => {
          const base64 = dataUrl.split(",")[1]?.replace(/\s/g, "").trim();
          if (!base64) return null;
          try {
            const cleaned = base64.replace(/[^A-Za-z0-9+/=_-]/g, "");
            const safeBase64 = cleaned.replace(/-/g, "+").replace(/_/g, "/");
            const padLength = (4 - (safeBase64.length % 4)) % 4;
            const padded = safeBase64 + "=".repeat(padLength);
            const raw = atob(padded);
            return new Blob([new Uint8Array([...raw].map((c) => c.charCodeAt(0)))], {
              type: "image/jpeg",
            });
          } catch {
            return null;
          }
        };

        const resolvePhotoToBlob = async (
          photo: { imageUrl: string; fileName: string }
        ): Promise<{ photo: typeof photo; blob: Blob } | null> => {
          let dataUrl: string;
          if (typeof photo.imageUrl === "string" && photo.imageUrl.startsWith("data:")) {
            dataUrl = photo.imageUrl;
          } else {
            dataUrl = await loadPhotoAsUrl(photo.imageUrl);
            if (!dataUrl || !dataUrl.startsWith("data:")) {
              addLog(`Could not load photo from path: ${photo.imageUrl}`);
              return null;
            }
          }
          const blob = dataUrlToBlob(dataUrl);
          if (!blob || blob.size === 0) {
            addLog(`Skipping empty or invalid image: ${photo.fileName}`);
            return null;
          }
          return { photo, blob };
        };

        const resolved = await Promise.all(
          photos.map((photo) => resolvePhotoToBlob(photo))
        );
        const toUpload = resolved.filter(
          (r): r is { photo: { imageUrl: string; fileName: string }; blob: Blob } =>
            r != null
        );

        const uploadResults = await Promise.allSettled(
          toUpload.map(({ photo, blob }) =>
            addCaseImageAsync({
              entityId: caseId,
              binary: blob,
              filename: photo.fileName,
              csrfToken,
            })
          )
        );

        uploadResults.forEach((result, i) => {
          const { photo } = toUpload[i];
          if (result.status === "fulfilled") {
            addLog(`Successfully uploaded image: ${photo.fileName}`);
            uploadedCount++;
          } else {
            addLog(
              `Failed to upload image ${photo.fileName}: ${result.reason}`
            );
            failedCount++;
          }
        });
        failedCount += photos.length - toUpload.length;
      }

      const photoSummary =
        photos.length > 0
          ? failedCount === 0
            ? ` and ${uploadedCount} image(s)`
            : `; ${uploadedCount} image(s) uploaded${failedCount > 0 ? `, ${failedCount} failed` : ""}`
          : "";
      toast({
        title: "Case Submitted",
        description: `Your grievance case${photoSummary} submitted successfully.`,
      });
      if (failedCount > 0) {
        toast({
          title: "Some images could not be uploaded",
          description: "Check Capacitor/Console in logcat for loadPhotoAsUrl or upload errors.",
          variant: "destructive",
        });
      }

      // Clear form data and photos after successful submission
      clearFormData();
      clearPhotos();
      setPhotoUrls({});

      skipNextSave.current = true;
      form.reset({
        title: "",
        parish: "",
        operator: "",
        community: "",
        description: "",
        impact: "",
        reportedBy: "",
        tags: [],
      });

      navigate("/");
    } catch (error: unknown) {
      addLog(`Submission error: ${JSON.stringify(error, null, 2)}`);
      const err = error as { status?: number; message?: string; data?: unknown };
      const status = err?.status;
      const data = err?.data;
      let description = "There was an error submitting your case or images.";
      if (status === 401) {
        description = "You are not logged in. Please log in and try again.";
      } else if (status === 403) {
        description = "Access denied. Check your account permissions.";
      } else if (status === 422 && data && typeof data === "object") {
        const msg = (data as { errors?: Array<{ detail?: string }> }).errors?.[0]?.detail;
        description = msg ? `Validation: ${msg}` : description;
      } else if (status != null) {
        description = `Request failed (${status}). Check console for details.`;
      } else if (err?.message?.includes("Failed to fetch") || err?.message?.includes("NetworkError")) {
        description = "Network error. Is the backend reachable? If local, check CORS and VITE_API_BASE_URL.";
      }
      toast({
        title: "Submission Failed",
        description,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToAddPhoto = () => {
    navigate("/add-photo");
  };

  const handleRemovePhoto = (photoId: string) => {
    removePhoto(photoId);
    toast({
      title: "Photo Removed",
      description: "The photo has been removed from your grievance.",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header title="Add New Grievance" showBackButton />

      <main className="px-4 pt-28 pb-24">
        {isLoading ? (
          <LoadingState message="Loading form data..." />
        ) : isError ? (
          <div className="text-center py-8 border border-dashed rounded-lg">
            <p className="text-muted-foreground">
              Error loading form data. Please try again.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="mt-3"
            >
              Retry
            </Button>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              {/* Title field */}
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="E.g., Water Contamination in Rio Minho"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Parish selection */}
              <FormField
                control={form.control}
                name="parish"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parish</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a parish" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {parishes.map((parish) => (
                          <SelectItem key={parish} value={parish}>
                            {parish}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Mining operator selection */}
              <FormField
                control={form.control}
                name="operator"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mining Operator</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a operator" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {operators.map((operator) => (
                          <SelectItem key={operator} value={operator}>
                            {operator}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Community field */}
              <FormField
                control={form.control}
                name="community"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Community</FormLabel>
                    <FormControl>
                      <Input placeholder="Your community name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Tags selection */}
              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags (Select all that apply)</FormLabel>
                    <FormControl>
                      <ToggleGroup
                        type="multiple"
                        variant="outline"
                        className="flex flex-wrap gap-2 justify-start"
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        {availableTags.map((tag) => (
                          <ToggleGroupItem
                            key={tag.value}
                            value={tag.value}
                            className="flex-grow-0 border-primary text-center rounded-md"
                          >
                            {tag.label}
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description field */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Provide details about the grievance..."
                        className="min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Impact field */}
              <FormField
                control={form.control}
                name="impact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Impact (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe how this has affected you, your community, or the environment..."
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Reported By field */}
              <FormField
                control={form.control}
                name="reportedBy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reported By (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Person or organization name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Photos section */}
              {hasPhotos && (
                <FormItem>
                  <FormLabel>Supporting Photos ({photoCount})</FormLabel>
                  <div className="space-y-3">
                    {photos.map((photo) => (
                      <div key={photo.id} className="space-y-2">
                        <div className="relative bg-black rounded-lg overflow-hidden aspect-[4/3] max-w-sm">
                          <img
                            src={photoUrls[photo.id] || ""}
                            alt="Supporting evidence"
                            className="w-full h-full object-contain"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemovePhoto(photo.id)}
                            className="absolute top-2 right-2 h-8 w-8 p-0"
                          >
                            <X size={16} />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Photo: {photo.fileName}
                        </p>
                      </div>
                    ))}
                  </div>
                </FormItem>
              )}

              {/* Photo button and Submit button */}
              <div className="flex flex-col gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToAddPhoto}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Camera size={18} />
                  {hasPhotos
                    ? `Add Another Photo (${photoCount} added)`
                    : "Add Supporting Photo"}
                </Button>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  {isSubmitting ? "Submitting..." : "Submit Grievance"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </main>
    </div>
  );
};

export default AddCase;
